import type { PointerEvent as ReactPointerEvent, RefObject, SyntheticEvent } from 'react';
import { useEffect, useRef } from 'react';

import { emitToHost } from '../lib/bridge.js';

/** Distance from the viewport's bottom-right corner, beyond the root's own inset. */
interface Offset {
  right: number;
  bottom: number;
}

const KEY = 'ajt-chat:widget-position';
const THRESHOLD = 4;

function load(): Offset {
  try {
    const p = JSON.parse(sessionStorage.getItem(KEY) ?? '') as Partial<Offset>;
    if (ok(p.right) && ok(p.bottom)) return { right: p.right, bottom: p.bottom };
  } catch {
    /* nothing remembered */
  }
  return { right: 0, bottom: 0 };
}

function ok(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0;
}

function save(o: Offset): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(o));
  } catch {
    /* storage unavailable — the position lasts the page */
  }
}

function apply(el: HTMLElement, o: Offset | null): void {
  if (o) {
    el.style.setProperty('--drag-right', `${o.right}px`);
    el.style.setProperty('--drag-bottom', `${o.bottom}px`);
  } else {
    el.style.removeProperty('--drag-right');
    el.style.removeProperty('--drag-bottom');
  }
}

/** At rest the loader carries the offset when framed; standalone the root does. */
function rest(el: HTMLElement, o: Offset): void {
  apply(el, window.parent !== window ? null : o);
}

const clamp = (v: number, max: number) => Math.round(Math.max(0, Math.min(v, max)));

/**
 * Drag the widget by anything marked `data-drag-handle`; the root's position
 * reads the `--drag-*` vars this sets. Offsets are distances to the viewport's
 * right/bottom edges, so they read the same in the small frame and the stretched one.
 */
export function useDrag(root: RefObject<HTMLElement | null>) {
  const offset = useRef<Offset | null>(null);
  offset.current ??= load();
  const dragged = useRef(false);

  useEffect(() => {
    const o = offset.current!;
    if ((!o.right && !o.bottom) || !root.current) return;
    emitToHost('position', { ...o, dragging: false });
    rest(root.current, o);
  }, [root]);

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    dragged.current = false;
    const el = root.current;
    const target = e.target as Element;
    const handle = target.closest<HTMLElement>('[data-drag-handle]');
    const control = target.closest('button, a, input, textarea, select');
    if (
      !el ||
      !handle ||
      (control && control !== handle) ||
      e.button !== 0 ||
      document.documentElement.dataset.viewport === 'mobile'
    )
      return;

    const rect = el.getBoundingClientRect();
    const grab = { right: rect.right - e.clientX, bottom: rect.bottom - e.clientY };
    const inset = {
      right:
        window.innerWidth -
        rect.right -
        (parseFloat(el.style.getPropertyValue('--drag-right')) || 0),
      bottom:
        window.innerHeight -
        rect.bottom -
        (parseFloat(el.style.getPropertyValue('--drag-bottom')) || 0),
    };
    const span = { w: rect.width + inset.right * 2, h: rect.height + inset.bottom * 2 };
    const start = { x: e.clientX, y: e.clientY };
    const ac = new AbortController();

    const move = (ev: PointerEvent) => {
      if (!dragged.current) {
        if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < THRESHOLD) return;
        dragged.current = true;
        emitToHost('position', { ...offset.current!, dragging: true });
        apply(el, offset.current!);
      }
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // No room to move means the host has not stretched the frame yet.
      if (vw <= span.w || vh <= span.h) return;
      offset.current = {
        right: clamp(vw - ev.clientX - grab.right - inset.right, vw - span.w),
        bottom: clamp(vh - ev.clientY - grab.bottom - inset.bottom, vh - span.h),
      };
      apply(el, offset.current);
    };
    const up = () => {
      ac.abort();
      if (!dragged.current) return;
      // The click that ends the drag has fired by the time a timer runs.
      setTimeout(() => (dragged.current = false));
      const o = offset.current!;
      save(o);
      emitToHost('position', { ...o, dragging: false });
      // Hand the offset back to the loader once it has shrunk the frame around
      // it; clearing sooner snaps the widget to the corner for a frame.
      const settle = () => {
        clearTimeout(timer);
        window.removeEventListener('resize', settle);
        rest(el, o);
      };
      const timer = setTimeout(settle, 300);
      window.addEventListener('resize', settle);
    };
    handle.setPointerCapture(e.pointerId);
    handle.addEventListener('pointermove', move, { signal: ac.signal });
    handle.addEventListener('pointerup', up, { signal: ac.signal });
    handle.addEventListener('pointercancel', up, { signal: ac.signal });
  };

  // The click that ends a drag is not a tap on whatever was dragged.
  const onClickCapture = (e: SyntheticEvent) => {
    if (!dragged.current) return;
    e.preventDefault();
    e.stopPropagation();
  };

  return { onPointerDown, onClickCapture };
}
